// "Pending overlay" (design note): the mirror tables hold the SERVER's numbers. Whatever the outbox has not yet been folded into the
// server state is expressed as pendingStock / pendingBalance / pendingPayable on the rows, always recomputed from scratch from the
// outbox (a pure function of its contents), so it can never drift. Screens show   server value + pending delta.
import { D, Decimal, ZERO, countAdjustment, computeReturn, computeSale, walletDelta, loanDelta } from '@dd/shared';
import type { OpPayload } from '@dd/shared';
import { db, metaGet, metaSet } from '@/db';
import type { ShopDB } from '@/db/schema';
import type { OutboxItem, Product, SaleItem, SalePayment, Sale } from '@/db/types';

// payloads of the category-module ops are validated by spec-generated schemas, so they are loosely typed here
type MP = Record<string, any>;
type Field = 'pendingStock' | 'pendingBalance' | 'pendingPayable' | 'pendingStatus';
interface Cell { table: string; id: string; field: Field; base: Decimal; cur: Decimal }

const BASE_FIELD: Record<Field, string> = { pendingStock: 'onHand', pendingBalance: 'balance', pendingPayable: 'payable', pendingStatus: 'status' };

class Overlay {
  cells = new Map<string, Cell>();
  statuses = new Map<string, string>();
  constructor(private d: ShopDB) {}

  private async cell(table: string, id: string, field: Field): Promise<Cell | null> {
    const k = `${table}|${id}|${field}`;
    let c = this.cells.get(k);
    if (c) return c;
    const row = await this.d.table(table).get(id);
    if (!row) return null;
    const base = D((row as Record<string, string | undefined>)[BASE_FIELD[field]] ?? 0);
    c = { table, id, field, base, cur: base };
    this.cells.set(k, c);
    return c;
  }
  async add(table: string, id: string | null | undefined, field: Field, delta: Decimal.Value): Promise<void> {
    if (!id) return;
    const c = await this.cell(table, id, field);
    if (c) c.cur = c.cur.plus(delta);
  }
  async set(table: string, id: string, field: Field, value: Decimal.Value): Promise<void> {
    const c = await this.cell(table, id, field);
    if (c) c.cur = D(value);
  }
  /** Current running figure (server + earlier pending ops), used by 'count' adjustments */
  async current(table: string, id: string, field: Field): Promise<Decimal> {
    return (await this.cell(table, id, field))?.cur ?? ZERO;
  }
  status(id: string, s: string): void { this.statuses.set(id, s); }
}

const stockTargets = async (o: Overlay, p: { productId?: string | null; variantId?: string | null; batchId?: string | null }, delta: Decimal): Promise<void> => {
  if (p.productId) await o.add('products', p.productId, 'pendingStock', delta);
  if (p.variantId) await o.add('product_variants', p.variantId, 'pendingStock', delta);
  if (p.batchId) await o.add('stock_batches', p.batchId, 'pendingStock', delta);
};

/** Σ payments capped at the total, in order — mirrors the server (change handed back is not revenue). */
export function cappedPayments(pays: Array<{ id?: string; amount: string; moneyAccountId: string }>, total: Decimal.Value): Array<{ moneyAccountId: string; amount: Decimal }> {
  let left = D(total);
  const out: Array<{ moneyAccountId: string; amount: Decimal }> = [];
  for (const p of pays) {
    const take = Decimal.min(D(p.amount), left);
    if (take.lte(0)) continue;
    left = left.minus(take);
    out.push({ moneyAccountId: p.moneyAccountId, amount: take });
  }
  return out;
}

async function saleEffects(o: Overlay, d: ShopDB, saleId: string, sign: 1 | -1): Promise<void> {
  const sale = (await d.sales.get(saleId)) as Sale | undefined;
  if (!sale) return;
  const items = (await d.sale_items.where('saleId').equals(saleId).toArray()) as SaleItem[];
  for (const it of items) {
    const prod = it.productId ? ((await d.products.get(it.productId)) as Product | undefined) : undefined;
    if (!prod?.trackStock) continue;
    const base = D(it.qty).times(it.unitFactor).times(sign === 1 ? -1 : 1);
    await stockTargets(o, { productId: it.productId, variantId: it.variantId, batchId: it.batchId }, base);
    if (it.serialId) o.status(it.serialId, sign === 1 ? 'sold' : 'in_stock');
  }
  const pays = (await d.sale_payments.where('saleId').equals(saleId).toArray()) as SalePayment[];
  for (const p of pays) await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', D(p.amount).times(sign));
  if (sale.customerId && D(sale.dueAmount).gt(0)) await o.add('customers', sale.customerId, 'pendingBalance', D(sale.dueAmount).times(sign));
}

async function effectOf(o: Overlay, d: ShopDB, op: OutboxItem): Promise<void> {
  switch (op.type) {
    case 'sale.create': {
      const p = op.payload as OpPayload<'sale.create'>;
      // the sale row was written optimistically (items, payments); the effects are derived from those rows
      const sale = await d.sales.get(op.entityId);
      if (!sale) return;
      await saleEffects(o, d, op.entityId, 1);
      void p;
      return;
    }
    case 'sale.void': await saleEffects(o, d, op.entityId, -1); return;
    case 'sale.restore': await saleEffects(o, d, op.entityId, 1); return;
    case 'return.create': {
      const r = await d.sale_returns.get(op.entityId);
      if (!r) return;
      await returnEffects(o, d, op.entityId, 1);
      return;
    }
    case 'return.void': await returnEffects(o, d, op.entityId, -1); return;
    case 'baki.credit': {
      const p = op.payload as OpPayload<'baki.credit'>;
      await o.add('customers', p.customerId, 'pendingBalance', p.amount);
      return;
    }
    case 'baki.payment': {
      const p = op.payload as OpPayload<'baki.payment'>;
      await o.add('customers', p.customerId, 'pendingBalance', D(p.amount).negated());
      await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', p.amount);
      return;
    }
    case 'baki.writeoff': {
      const p = op.payload as OpPayload<'baki.writeoff'>;
      await o.add('customers', p.customerId, 'pendingBalance', D(p.amount).negated());
      return;
    }
    case 'customer.upsert': {
      const p = op.payload as OpPayload<'customer.upsert'>;
      if (p.openingBalance && D(p.openingBalance).gt(0) && op.baseVersion === undefined) await o.add('customers', op.entityId, 'pendingBalance', p.openingBalance);
      return;
    }
    case 'product.upsert': {
      const p = op.payload as OpPayload<'product.upsert'>;
      if (p.opening && D(p.opening.qty).gt(0) && p.trackStock && op.baseVersion === undefined) {
        const batchId = openingBatchId(op);
        await stockTargets(o, { productId: op.entityId, variantId: p.opening.variantId, batchId }, D(p.opening.qty));
      }
      return;
    }
    case 'stock.adjust': {
      const p = op.payload as OpPayload<'stock.adjust'>;
      const [table, id] = p.batchId ? ['stock_batches', p.batchId] : p.variantId ? ['product_variants', p.variantId] : ['products', p.productId];
      const cur = await o.current(table, id, 'pendingStock');
      const delta = p.mode === 'count' ? countAdjustment(p.qty, cur) : D(p.qty).negated();
      await stockTargets(o, { productId: p.productId, variantId: p.variantId, batchId: p.batchId }, delta);
      return;
    }
    case 'purchase.create': {
      const p = op.payload as OpPayload<'purchase.create'>;
      const subtotal = p.items.reduce((a, i) => a.plus(D(i.qty).times(i.unitCost)), ZERO);
      const total = subtotal.minus(p.discount);
      await o.add('vendors', p.vendorId, 'pendingPayable', total);
      if (D(p.paidAtPurchase).gt(0)) {
        await o.add('vendors', p.vendorId, 'pendingPayable', D(p.paidAtPurchase).negated());
        await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', D(p.paidAtPurchase).negated());
      }
      for (const it of p.items) {
        const prod = (await d.products.get(it.productId)) as Product | undefined;
        if (!prod?.trackStock) continue;
        const batchId = await purchaseBatchId(d, op, it);
        await stockTargets(o, { productId: it.productId, variantId: it.variantId, batchId }, D(it.qty).times(it.unitFactor));
      }
      return;
    }
    case 'purchase.void': {
      const pu = await d.purchases.get(op.entityId);
      if (!pu) return;
      const items = await d.purchase_items.where('purchaseId').equals(op.entityId).toArray();
      await o.add('vendors', pu.vendorId, 'pendingPayable', D(pu.total).negated());
      if (D(pu.paidAtPurchase).gt(0)) {
        await o.add('vendors', pu.vendorId, 'pendingPayable', D(pu.paidAtPurchase));
        await o.add('money_accounts', pu.moneyAccountId, 'pendingBalance', pu.paidAtPurchase);
      }
      for (const it of items) {
        const prod = (await d.products.get(it.productId)) as Product | undefined;
        if (!prod?.trackStock) continue;
        const b = it.batchNo || it.expiryDate ? await d.stock_batches.where('productId').equals(it.productId).filter((x) => (x.batchNo ?? null) === (it.batchNo ?? null) && (x.expiryDate ?? null) === (it.expiryDate ?? null)).first() : undefined;
        await stockTargets(o, { productId: it.productId, variantId: it.variantId, batchId: b?.id }, D(it.qty).times(it.unitFactor).negated());
      }
      return;
    }
    case 'vendor.upsert': {
      const p = op.payload as OpPayload<'vendor.upsert'>;
      if (p.openingPayable && D(p.openingPayable).gt(0) && op.baseVersion === undefined) await o.add('vendors', op.entityId, 'pendingPayable', p.openingPayable);
      return;
    }
    case 'vendor.payment': {
      const p = op.payload as OpPayload<'vendor.payment'>;
      await o.add('vendors', p.vendorId, 'pendingPayable', D(p.amount).negated());
      await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', D(p.amount).negated());
      return;
    }
    case 'vendor.return': {
      const p = op.payload as OpPayload<'vendor.return'>;
      let total = ZERO;
      for (const it of p.items) {
        total = total.plus(D(it.qty).times(it.unitCost));
        const prod = (await d.products.get(it.productId)) as Product | undefined;
        if (prod?.trackStock) await stockTargets(o, { productId: it.productId, batchId: it.batchId }, D(it.qty).negated());
      }
      if (p.settlement === 'payable_adjust') await o.add('vendors', p.vendorId, 'pendingPayable', total.negated());
      else await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', total);
      return;
    }
    case 'expense.create': {
      const p = op.payload as OpPayload<'expense.create'>;
      await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', D(p.amount).negated());
      return;
    }
    case 'expense.void': {
      const e = await d.expenses.get(op.entityId);
      if (e) await o.add('money_accounts', e.moneyAccountId, 'pendingBalance', e.amount);
      return;
    }
    case 'expense.edit': {
      const p = op.payload as OpPayload<'expense.edit'>;
      const old = before(op, 'expenses', op.entityId) ?? (await d.expenses.get(op.entityId));
      if (old && !old.voidedAt) await o.add('money_accounts', old.moneyAccountId as string, 'pendingBalance', old.amount as string);
      await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', D(p.amount).negated());
      return;
    }
    case 'money.transfer': {
      const p = op.payload as OpPayload<'money.transfer'>;
      await o.add('money_accounts', p.fromAccountId, 'pendingBalance', D(p.amount).negated());
      await o.add('money_accounts', p.toAccountId, 'pendingBalance', p.amount);
      return;
    }
    case 'money.adjust': {
      const p = op.payload as OpPayload<'money.adjust'>;
      await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', p.amount);
      return;
    }
    // ── category modules: the float of a wallet, personal money and loans ──
    case 'mfs_txn.create': {
      const p = op.payload as MP;
      await o.add('mfs_wallets', p.walletId, 'pendingBalance', walletDelta(p.type, p.amount));
      return;
    }
    case 'mfs_txn.void': {
      const t = await d.table('mfs_txns').get(op.entityId);
      if (t) await o.add('mfs_wallets', t.walletId, 'pendingBalance', walletDelta(t.type, t.amount).negated());
      return;
    }
    case 'recharge.create': {
      const p = op.payload as MP;
      if (p.walletId) await o.add('mfs_wallets', p.walletId, 'pendingBalance', D(p.amount).negated());
      return;
    }
    case 'recharge.void': {
      const r = await d.table('recharges').get(op.entityId);
      if (r?.walletId) await o.add('mfs_wallets', r.walletId, 'pendingBalance', r.amount);
      return;
    }
    case 'personal_entry.create': {
      const p = op.payload as MP;
      await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', p.entryType === 'income' ? p.amount : D(p.amount).negated());
      return;
    }
    case 'personal_entry.void': {
      const e = await d.table('personal_entries').get(op.entityId);
      if (e) await o.add('money_accounts', e.moneyAccountId, 'pendingBalance', e.entryType === 'income' ? D(e.amount).negated() : e.amount);
      return;
    }
    case 'loan.create': {
      const p = op.payload as MP;
      if (p.moneyAccountId) await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', loanDelta(p.direction, p.amount));
      return;
    }
    case 'loan.repay': {
      const p = op.payload as MP;
      const loan = await d.table('loans').get(p.loanId);
      if (!loan) return;
      await o.add('loans', p.loanId, 'pendingBalance', D(p.amount).negated());
      const acc = p.moneyAccountId ?? loan.moneyAccountId;
      if (acc) await o.add('money_accounts', acc, 'pendingBalance', loanDelta(loan.direction, p.amount, true));
      return;
    }
    // ── v10 corrections: "before" comes from the op's own undo snapshot (the row as it was when this op was applied) ──
    case 'personal_entry.edit': {
      const p = op.payload as MP;
      const e = before(op, 'personal_entries', op.entityId);
      if (e && !e.voidedAt) await o.add('money_accounts', e.moneyAccountId, 'pendingBalance', e.entryType === 'income' ? D(e.amount).negated() : e.amount);
      await o.add('money_accounts', p.moneyAccountId, 'pendingBalance', p.entryType === 'income' ? p.amount : D(p.amount).negated());
      return;
    }
    case 'personal_entry.restore': {
      const e = before(op, 'personal_entries', op.entityId);
      if (e?.voidedAt) await o.add('money_accounts', e.moneyAccountId, 'pendingBalance', e.entryType === 'income' ? e.amount : D(e.amount).negated());
      return;
    }
    case 'loan.update': {
      const p = op.payload as MP;
      const l = before(op, 'loans', op.entityId);
      if (!l) return;
      const acc = p.moneyAccountId === undefined ? l.moneyAccountId : p.moneyAccountId;
      await o.add('loans', op.entityId, 'pendingBalance', D(p.amount).minus(l.amount));
      if ((acc ?? null) !== (l.moneyAccountId ?? null) || !D(p.amount).eq(l.amount)) {
        if (l.moneyAccountId) await o.add('money_accounts', l.moneyAccountId, 'pendingBalance', loanDelta(l.direction, l.amount).negated());
        if (acc) await o.add('money_accounts', acc, 'pendingBalance', loanDelta(l.direction, p.amount));
      }
      return;
    }
    case 'loan.delete': case 'loan.restore': {
      const l = before(op, 'loans', op.entityId);
      if (!l) return;
      const sign = op.type === 'loan.delete' ? -1 : 1; // delete takes the loan's money back, restore books it again
      if (op.type === 'loan.delete' ? l.deletedAt : !l.deletedAt) return;
      if (l.moneyAccountId) await o.add('money_accounts', l.moneyAccountId, 'pendingBalance', loanDelta(l.direction, l.amount).times(sign));
      for (const u of op.undo) if (u.table === 'loan_repayments' && u.before) {
        const r = u.before as MP;
        if (r.moneyAccountId) await o.add('money_accounts', r.moneyAccountId, 'pendingBalance', loanDelta(l.direction, r.amount, true).times(sign));
      }
      return;
    }
    case 'loan_repayment.void': {
      const r = before(op, 'loan_repayments', op.entityId);
      if (!r || r.deletedAt) return;
      const loan = await d.table('loans').get(r.loanId);
      if (!loan) return;
      await o.add('loans', r.loanId, 'pendingBalance', r.amount);
      if (r.moneyAccountId) await o.add('money_accounts', r.moneyAccountId, 'pendingBalance', loanDelta(loan.direction, r.amount, true).negated());
      return;
    }
    case 'savings_goal.contribute': {
      const p = op.payload as MP;
      const g = before(op, 'savings_goals', op.entityId);
      if (!g?.accountId || !p.accountId || p.accountId === g.accountId) return;
      const [from, to] = p.kind === 'deposit' ? [p.accountId, g.accountId] : [g.accountId, p.accountId];
      await o.add('money_accounts', from, 'pendingBalance', D(p.amount).negated());
      await o.add('money_accounts', to, 'pendingBalance', p.amount);
      return;
    }
    default: return;
  }
}

/** The row as this op found it (its first undo snapshot) — the "before" picture an edit / delete is measured against. */
function before(op: OutboxItem, table: string, id: string): MP | null {
  return (op.undo.find((u) => u.table === table && u.id === id)?.before as MP | undefined) ?? null;
}

async function returnEffects(o: Overlay, d: ShopDB, returnId: string, sign: 1 | -1): Promise<void> {
  const r = await d.sale_returns.get(returnId);
  if (!r) return;
  const sale = (await d.sales.get(r.saleId)) as Sale | undefined;
  const lines = await d.sale_return_items.where('returnId').equals(returnId).toArray();
  for (const l of lines) {
    if (!l.restock) continue;
    const it = (await d.sale_items.get(l.saleItemId)) as SaleItem | undefined;
    if (!it?.productId) continue;
    const prod = (await d.products.get(it.productId)) as Product | undefined;
    if (!prod?.trackStock) continue;
    await stockTargets(o, { productId: it.productId, variantId: it.variantId, batchId: it.batchId }, D(l.qty).times(it.unitFactor).times(sign));
  }
  if (sale?.customerId && D(r.bakiAdjust).gt(0)) await o.add('customers', sale.customerId, 'pendingBalance', D(r.bakiAdjust).times(-sign));
  if (r.moneyAccountId && D(r.refundCash).gt(0)) await o.add('money_accounts', r.moneyAccountId, 'pendingBalance', D(r.refundCash).times(-sign));
}

/** Batch created by product.upsert(opening): derivedId(opId, 0) on the server too. */
export function openingBatchId(op: Pick<OutboxItem, 'opId' | 'payload'>): string | null {
  const p = op.payload as OpPayload<'product.upsert'>;
  if (!p.opening || !(p.hasBatches || p.opening.batchNo || p.opening.expiryDate)) return null;
  return derive(op.opId, 0);
}
import { derivedId } from '@dd/shared';
const derive = derivedId;

/** Which batch a purchase line lands in: an existing one with the same number+expiry, else the one the server will create. */
async function purchaseBatchId(d: ShopDB, op: OutboxItem, item: OpPayload<'purchase.create'>['items'][number]): Promise<string | null> {
  const prod = (await d.products.get(item.productId)) as Product | undefined;
  if (!(item.batchNo || item.expiryDate || prod?.hasBatches)) return null;
  const found = await d.stock_batches.where('productId').equals(item.productId).filter((x) => (x.batchNo ?? null) === (item.batchNo ?? null) && (x.expiryDate ?? null) === (item.expiryDate ?? null)).first();
  if (found) return found.id;
  return purchaseBatchIds(op).get(item.id) ?? null;
}
/** Re-walks the purchase payload to reproduce the server's nextId() sequence → batch id per item id. */
export function purchaseBatchIds(op: Pick<OutboxItem, 'opId' | 'payload'>, existing: (item: OpPayload<'purchase.create'>['items'][number]) => boolean = () => false, tracked: (productId: string) => boolean = () => true): Map<string, string> {
  const p = op.payload as OpPayload<'purchase.create'>;
  const out = new Map<string, string>();
  let n = 0;
  const made = new Map<string, string>();
  for (const it of p.items) {
    if (it.batchNo || it.expiryDate) {
      const key = `${it.productId}|${it.batchNo ?? ''}|${it.expiryDate ?? ''}`;
      if (existing(it)) { /* reuses an existing batch: no id consumed */ } else if (made.has(key)) out.set(it.id, made.get(key)!);
      else { const id = derivedId(op.opId, n++); made.set(key, id); out.set(it.id, id); }
    }
    n += (it.serialNos ?? []).length;
    if (tracked(it.productId)) n += 1;
  }
  return out;
}

/** Rebuild every pending* field from the outbox. Runs inside the caller's transaction when there is one. */
export async function recomputeOverlay(): Promise<void> {
  const d = db();
  await d.transaction('rw', d.tables, async () => {
    // 1. wipe the previous overlay
    const old = (await metaGet<Array<[string, string]>>('overlayKeys')) ?? [];
    for (const [table, id] of old) {
      const row = (await d.table(table).get(id)) as Record<string, unknown> | undefined;
      if (!row) continue;
      delete row.pendingStock; delete row.pendingBalance; delete row.pendingPayable; delete row.pendingStatus;
      await d.table(table).put(row);
    }
    // 2. fold the outbox (oldest first) into running figures
    const ops = (await d.outbox.orderBy('deviceSeq').toArray()).filter((x) => x.state !== 'rejected');
    const o = new Overlay(d);
    for (const op of ops) await effectOf(o, d, op);
    // 3. write the differences
    const keys: Array<[string, string]> = [];
    const merged = new Map<string, Record<string, unknown>>();
    for (const c of o.cells.values()) {
      const delta = c.cur.minus(c.base);
      if (delta.isZero()) continue;
      const k = `${c.table}|${c.id}`;
      const row = merged.get(k) ?? ((await d.table(c.table).get(c.id)) as Record<string, unknown>);
      row[c.field] = delta.toString();
      merged.set(k, row);
    }
    for (const [id, status] of o.statuses) {
      const row = (await d.serial_units.get(id)) as unknown as Record<string, unknown> | undefined;
      if (!row || row.status === status) continue;
      const k = `serial_units|${id}`;
      const r = merged.get(k) ?? row;
      r.pendingStatus = status;
      merged.set(k, r);
    }
    for (const [k, row] of merged) {
      const [table, id] = k.split('|') as [string, string];
      await d.table(table).put(row);
      keys.push([table, id]);
    }
    await metaSet('overlayKeys', keys);
  });
}

// ── read-side helpers: server value + pending ─────────────────────────────────
export const stockOf = (r: { onHand: string; pendingStock?: string }): Decimal => D(r.onHand).plus(r.pendingStock ?? 0);
export const balanceOf = (r: { balance?: string; pendingBalance?: string }): Decimal => D(r.balance ?? 0).plus(r.pendingBalance ?? 0);
export const payableOf = (r: { payable: string; pendingPayable?: string }): Decimal => D(r.payable).plus(r.pendingPayable ?? 0);
export const serialStatusOf = (r: { status: string; pendingStatus?: string }): string => r.pendingStatus ?? r.status;
void computeReturn; void computeSale;
